"use client";

import { composeRefs } from "@radix-ui/react-compose-refs";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import clsx from "clsx";
import React from "react";
import FloatingPanel from "../../common/FloatingPanel/FloatingPanel";
import { DEFAULT_FLOATING_SIDE_OFFSET } from "../../common/FloatingPanel/positioning";
import Calendar from "./Calendar";
import styles from "./DateTimeInput.module.scss";
import { getPortalContainer } from "./PickerPopover";
import {
  TimeList,
  type TimePreset,
  type TimePresetLayout,
  buildTimeOptions,
  findTimeOptionIndex,
  getColumnSwitchIndex,
  getNextTimeOptionIndexInLayout,
  getTimeOptionId,
} from "./TimeList";
import { type TimeOfDay, combineDateTime, getLocaleHour12, startOfDay, timeOf } from "./dateTimeFormat";

type Placement =
  | "top"
  | "right"
  | "bottom"
  | "left"
  | "top-start"
  | "top-end"
  | "right-start"
  | "right-end"
  | "bottom-start"
  | "bottom-end"
  | "left-start"
  | "left-end";

type DateTimePickerContextValue = {
  value: Date | null;
  onValueChange?: (value: Date | null) => void;
  dateOnly: boolean;
  minDate?: Date;
  maxDate?: Date;
  disabledDates?: Date[];
  disabled?: boolean;
  hour12?: boolean;
  locale?: string;
  timeStepMinutes: number | null;
  timePresets?: TimePreset[];
  timePresetLayout: TimePresetLayout;
  placement: Placement;
  close: () => void;
  open: boolean;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
};

const DateTimePickerContext = React.createContext<DateTimePickerContextValue | null>(null);

export interface DateTimePickerProps {
  value?: Date | null;
  onValueChange?: (value: Date | null) => void;
  dateOnly?: boolean;
  minDate?: Date;
  maxDate?: Date;
  disabledDates?: Date[];
  disabled?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  placement?: Placement;
  hour12?: boolean;
  locale?: string;
  /** Minutes between time options (default 15); null lists only the presets. */
  timeStepMinutes?: number | null;
  timePresets?: TimePreset[];
  timePresetLayout?: TimePresetLayout;
  children?: React.ReactNode;
}

/**
 * Popover with a calendar (and a time list unless `dateOnly`) opened from a custom trigger. For
 * a typed input use DateInput / DateTimeInput instead.
 */
const DateTimePickerRoot = ({
  value = null,
  onValueChange,
  dateOnly = false,
  minDate,
  maxDate,
  disabledDates,
  disabled,
  open: controlledOpen,
  onOpenChange,
  placement = "bottom-start",
  hour12,
  locale,
  timeStepMinutes = 15,
  timePresets,
  timePresetLayout = "side",
  children,
}: DateTimePickerProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = React.useCallback(
    (next: boolean) => {
      if (next && disabled) return;
      if (controlledOpen === undefined) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [controlledOpen, disabled, onOpenChange],
  );

  const contextValue = React.useMemo<DateTimePickerContextValue>(
    () => ({
      value,
      onValueChange,
      dateOnly,
      minDate,
      maxDate,
      disabledDates,
      disabled,
      hour12,
      locale,
      timeStepMinutes,
      timePresets,
      timePresetLayout,
      placement,
      close: () => setOpen(false),
      open,
      triggerRef,
    }),
    [
      value,
      onValueChange,
      dateOnly,
      minDate,
      maxDate,
      disabledDates,
      disabled,
      hour12,
      locale,
      timeStepMinutes,
      timePresets,
      timePresetLayout,
      placement,
      setOpen,
      open,
    ],
  );

  return (
    <DateTimePickerContext.Provider value={contextValue}>
      <PopoverPrimitive.Root
        open={open}
        onOpenChange={setOpen}
      >
        {children}
      </PopoverPrimitive.Root>
    </DateTimePickerContext.Provider>
  );
};

type TriggerProps = React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger> & {
  ref?: React.Ref<HTMLButtonElement>;
};

const DateTimePickerTrigger = ({ ref, ...props }: TriggerProps) => {
  const context = React.useContext(DateTimePickerContext);
  return (
    <PopoverPrimitive.Trigger
      ref={context ? composeRefs(ref, context.triggerRef) : ref}
      disabled={context?.disabled}
      {...props}
    />
  );
};

export interface DateTimePickerContentProps {
  className?: string;
  /** Overrides the Root value / handler, for content-controlled usage. */
  value?: Date | null;
  onValueChange?: (value: Date | null) => void;
}

const DateTimePickerContent = ({
  className,
  value: propsValue,
  onValueChange: propsOnValueChange,
}: DateTimePickerContentProps) => {
  const context = React.useContext(DateTimePickerContext);
  const listId = React.useId();
  const value = propsValue !== undefined ? propsValue : (context?.value ?? null);
  const onValueChange = propsOnValueChange ?? context?.onValueChange;
  const dateOnly = context?.dateOnly ?? false;
  const hour12 = React.useMemo(() => context?.hour12 ?? getLocaleHour12(context?.locale), [context]);
  const options = React.useMemo(
    () => buildTimeOptions(context ? context.timeStepMinutes : 15, context?.timePresets),
    [context?.timeStepMinutes, context?.timePresets],
  );
  const currentTime = value ? timeOf(value) : null;
  const [month, setMonth] = React.useState<Date>(value ?? new Date());
  const [activeIndex, setActiveIndex] = React.useState(() => findTimeOptionIndex(options, currentTime));

  if (!context) return null;

  const [side, align = "center"] = context.placement.split("-") as [
    "top" | "right" | "bottom" | "left",
    "start" | "end" | undefined,
  ];

  const selectDay = (day: Date | undefined) => {
    if (!day) return;
    if (dateOnly) {
      onValueChange?.(startOfDay(day));
      context.close();
      return;
    }
    onValueChange?.(combineDateTime(day, currentTime));
  };

  const selectTime = (time: TimeOfDay) => {
    onValueChange?.(combineDateTime(value ?? new Date(), time));
    context.close();
  };

  const handleListKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const nextIndex =
      getColumnSwitchIndex(event.key, activeIndex, options, context.timePresetLayout) ??
      getNextTimeOptionIndexInLayout(event.key, activeIndex, options, context.timePresetLayout);
    if (nextIndex !== null) {
      event.preventDefault();
      setActiveIndex(nextIndex);
    } else if ((event.key === "Enter" || event.key === " ") && options[activeIndex]) {
      event.preventDefault();
      selectTime(options[activeIndex].time);
    }
  };

  return (
    <PopoverPrimitive.Portal container={context.open ? getPortalContainer(context.triggerRef.current) : undefined}>
      <PopoverPrimitive.Content
        asChild
        side={side}
        align={align}
        sideOffset={DEFAULT_FLOATING_SIDE_OFFSET}
        collisionPadding={8}
        // The calendar moves focus to the selected day itself.
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <FloatingPanel
          open
          className={clsx(styles.popoverContent, className)}
        >
          <Calendar
            mode="single"
            autoFocus
            month={month}
            onMonthChange={setMonth}
            selected={value ?? undefined}
            onSelect={selectDay}
            startMonth={context.minDate}
            endMonth={context.maxDate}
            disabled={
              context.disabled || [
                ...(context.minDate ? [{ before: context.minDate }] : []),
                ...(context.maxDate ? [{ after: context.maxDate }] : []),
                ...(context.disabledDates ?? []),
              ]
            }
          />
          {!dateOnly && options.length ? (
            // The column takes the calendar's height; the list scrolls inside it instead of growing the panel.
            <div
              className={styles.timeListColumn}
              data-layout={
                context.timePresetLayout === "side" &&
                options.some((option) => option.label) &&
                options.some((option) => !option.label)
                  ? "side"
                  : "stacked"
              }
            >
              <TimeList
                id={listId}
                className={styles.timeListPanel}
                tabIndex={0}
                aria-activedescendant={activeIndex >= 0 ? getTimeOptionId(listId, activeIndex) : undefined}
                onKeyDown={handleListKeyDown}
                options={options}
                value={currentTime}
                activeIndex={activeIndex}
                onActiveIndexChange={setActiveIndex}
                onSelect={selectTime}
                hour12={hour12}
                presetLayout={context.timePresetLayout}
              />
            </div>
          ) : null}
        </FloatingPanel>
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  );
};

const DateTimePicker = Object.assign(DateTimePickerRoot, {
  /** Element that opens the picker; also its positioning anchor. */
  Reference: DateTimePickerTrigger,
  Trigger: DateTimePickerTrigger,
  Content: DateTimePickerContent,
});

export default DateTimePicker;
